import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Swal from "sweetalert2";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import {
  getAllRatingsAndReviews,
  getRatingReviewById,
  toggleRatingStatus,
  deleteRatingReview,
} from "../../api/ApiServices";

const ITEMS_PER_PAGE = 10;

// Helper: Render Star Icons
const renderStars = (rating) => {
  const stars = [];
  const roundedRating = Math.round(Number(rating) || 0);
  for (let i = 1; i <= 5; i++) {
    if (i <= roundedRating) {
      stars.push(
        <i
          key={i}
          className="fas fa-star text-warning mr-1"
          style={{ fontSize: "13px" }}
        />
      );
    } else {
      stars.push(
        <i
          key={i}
          className="far fa-star text-muted mr-1"
          style={{ fontSize: "13px", opacity: 0.5 }}
        />
      );
    }
  }
  return stars;
};

// Helper: Rating Badge Color
const getRatingColor = (rating) => {
  const num = Number(rating) || 0;
  if (num >= 4) return "success";
  if (num >= 3) return "warning";
  if (num >= 2) return "warning";
  return "danger";
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reviewTypeFilter, setReviewTypeFilter] = useState("all");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: ITEMS_PER_PAGE,
    totalRecords: 0,
    totalPages: 1,
  });

  const [summary, setSummary] = useState({
    totalReviews: 0,
    averageRating: 0,
    activeReviews: 0,
    inactiveReviews: 0,
  });

  const [selectedReview, setSelectedReview] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  // Fetch reviews from backend API
  const fetchReviews = useCallback(
    async (
      page = 1,
      search = searchTerm,
      rating = ratingFilter,
      status = statusFilter,
      reviewType = reviewTypeFilter
    ) => {
      try {
        setLoading(true);
        const response = await getAllRatingsAndReviews({
          page,
          limit: ITEMS_PER_PAGE,
          search: search.trim(),
          rating: rating === "all" ? "" : rating,
          isActive: status === "all" ? "" : status,
          reviewType: reviewType === "all" ? "" : reviewType,
        });

        if (response && response.success) {
          setReviews(response.data || []);
          if (response.pagination) {
            setPagination(response.pagination);
          }
          if (response.summary) {
            setSummary(response.summary);
          }
        } else {
          setReviews([]);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, ratingFilter, statusFilter, reviewTypeFilter]
  );

  // Debounce search and filter updates
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReviews(
        pagination.currentPage,
        searchTerm,
        ratingFilter,
        statusFilter,
        reviewTypeFilter
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [
    pagination.currentPage,
    searchTerm,
    ratingFilter,
    statusFilter,
    reviewTypeFilter,
    fetchReviews,
  ]);

  // Page change handler
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: page }));
    }
  };

  // View modal handler
  const openViewModal = async (review) => {
    setSelectedReview(review);
    setViewModalOpen(true);
    setViewLoading(true);

    try {
      const response = await getRatingReviewById(review.uuid);
      if (response && response.success && response.data) {
        setSelectedReview(response.data);
      }
    } catch (error) {
      console.error("Error fetching review detail:", error);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedReview(null);
  };

  // Toggle status handler
  const handleToggleStatus = async (uuid) => {
    try {
      const response = await toggleRatingStatus(uuid);
      if (response && response.success) {
        Swal.fire({
          icon: "success",
          title: "Status Updated",
          text: response.message || "Review status updated successfully",
          timer: 1500,
          showConfirmButton: false,
        });

        // Update locally
        setReviews((prev) =>
          prev.map((r) =>
            r.uuid === uuid ? { ...r, isActive: !r.isActive } : r
          )
        );

        if (selectedReview && selectedReview.uuid === uuid) {
          setSelectedReview((prev) => ({ ...prev, isActive: !prev.isActive }));
        }

        // Refetch to ensure summaries and ratings sync
        fetchReviews(pagination.currentPage, searchTerm, ratingFilter, statusFilter);
      }
    } catch (error) {
      console.error("Error toggling rating status:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error?.message || "Failed to update review status",
      });
    }
  };

  // Delete review handler
  const handleDeleteReview = (uuid) => {
    Swal.fire({
      title: "Delete Review?",
      text: "This action cannot be undone. Guide rating stats will be updated automatically.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f5365c",
      cancelButtonColor: "#8898aa",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteRatingReview(uuid);
          if (response && response.success) {
            Swal.fire("Deleted!", "The review has been removed.", "success");
            if (selectedReview && selectedReview.uuid === uuid) {
              closeViewModal();
            }
            fetchReviews(pagination.currentPage, searchTerm, ratingFilter, statusFilter);
          }
        } catch (error) {
          console.error("Error deleting review:", error);
          Swal.fire({
            icon: "error",
            title: "Delete Failed",
            text: error?.message || "Failed to delete review",
          });
        }
      }
    });
  };

  return (
    <>
      <CommonHeader />

      <Container className="mt--7" fluid>
        {/* KPI SUMMARY CARDS */}
        <Row className="mb-4">
          {/* Total Reviews */}
          <Col lg="4" md="4" className="mb-3 mb-md-0">
            <Card
              className="card-stats shadow border-0 h-100"
              role="button"
              tabIndex={0}
              onClick={() => {
                setStatusFilter("all");
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setStatusFilter("all");
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }
              }}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                border:
                  statusFilter === "all"
                    ? "2px solid #0356BB"
                    : "1px solid transparent",
                transform: statusFilter === "all" ? "translateY(-3px)" : "none",
              }}
            >
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <div className="col">
                    <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">
                      Total Reviews
                    </span>

                    <span className="h2 mb-0 font-weight-bold text-dark">
                      {summary.totalReviews || 0}
                    </span>
                  </div>

                  <Col className="col-auto">
                    <div
                      className="icon icon-shape text-white rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                      style={{
                        width: "42px",
                        height: "42px",
                        background: "#0356BB",
                      }}
                    >
                      <i className="fas fa-comments" />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Active Reviews */}
          <Col lg="4" md="4" className="mb-3 mb-md-0">
            <Card
              className="card-stats shadow border-0 h-100"
              role="button"
              tabIndex={0}
              onClick={() => {
                setStatusFilter("true");
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setStatusFilter("true");
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }
              }}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                border:
                  statusFilter === "true"
                    ? "2px solid #16a34a"
                    : "1px solid transparent",
                transform: statusFilter === "true" ? "translateY(-3px)" : "none",
              }}
            >
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <div className="col">
                    <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">
                      Active Reviews
                    </span>

                    <span className="h2 mb-0 font-weight-bold text-success">
                      {summary.activeReviews || 0}
                    </span>
                  </div>

                  <Col className="col-auto">
                    <div
                      className="icon icon-shape text-white rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                      style={{
                        width: "42px",
                        height: "42px",
                        background: "#16a34a",
                      }}
                    >
                      <i className="fas fa-check-circle" />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Hidden Reviews */}
          <Col lg="4" md="4" className="mb-3 mb-md-0">
            <Card
              className="card-stats shadow border-0 h-100"
              role="button"
              tabIndex={0}
              onClick={() => {
                setStatusFilter("false");
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setStatusFilter("false");
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }
              }}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                border:
                  statusFilter === "false"
                    ? "2px solid #8898aa"
                    : "1px solid transparent",
                transform: statusFilter === "false" ? "translateY(-3px)" : "none",
              }}
            >
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <div className="col">
                    <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">
                      Hidden Reviews
                    </span>

                    <span className="h2 mb-0 font-weight-bold text-muted">
                      {summary.inactiveReviews || 0}
                    </span>
                  </div>

                  <Col className="col-auto">
                    <div
                      className="icon icon-shape text-white rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                      style={{
                        width: "42px",
                        height: "42px",
                        background: "#8898aa",
                      }}
                    >
                      <i className="fas fa-eye-slash" />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* MAIN REVIEWS TABLE CARD */}
        <Row>
          <Col>
            <Card className="shadow border-0">
              <CardHeader className="border-0 bg-white pb-0 pt-4">
                <Row className="align-items-center justify-content-between">
                  <Col>
                    <h3 className="mb-0 text-dark font-weight-bold">
                      Reviews & Ratings Management
                    </h3>
                    <p className="text-muted text-xs mb-0 mt-1">
                      View full details of which traveler rated which guide, guide average ratings, total ratings, and feedback.
                    </p>
                  </Col>
                  <Col xs="auto">
                    <Badge color="primary" pill className="p-2 px-3">
                      Total: {pagination.totalRecords || 0}
                    </Badge>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {/* FILTER ROW */}
                <Row className="align-items-center mb-4">
                  {/* Search Input */}
                  <Col xs="12" md="5" lg="4" className="mb-2 mb-md-0">
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="fas fa-search" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        type="search"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setPagination((prev) => ({ ...prev, currentPage: 1 }));
                        }}
                        placeholder="Search by traveler, guide, review, booking #..."
                        className="px-3 py-2"
                      />
                    </InputGroup>
                  </Col>

                  {/* Review Type Filter */}
                  <Col xs="12" sm="6" md="3" lg="2" className="mb-2 mb-md-0">
                    <CustomDropdown
                      value={reviewTypeFilter}
                      onChange={(val) => {
                        setReviewTypeFilter(val);
                        setPagination((prev) => ({ ...prev, currentPage: 1 }));
                      }}
                      options={[
                        { label: "All Review Types", value: "all" },
                        { label: "Traveler → Guide", value: "traveler_to_guide" },
                        { label: "Guide → Traveler", value: "guide_to_traveler" },
                      ]}
                    />
                  </Col>

                  {/* Rating Filter */}
                  <Col xs="12" sm="6" md="3" lg="2" className="mb-2 mb-md-0">
                    <CustomDropdown
                      value={ratingFilter}
                      onChange={(val) => {
                        setRatingFilter(val);
                        setPagination((prev) => ({ ...prev, currentPage: 1 }));
                      }}
                      options={[
                        { label: "All Ratings", value: "all" },
                        { label: "5 Stars (★★★★★)", value: "5" },
                        { label: "4 Stars (★★★★)", value: "4" },
                        { label: "3 Stars (★★★)", value: "3" },
                        { label: "2 Stars (★★)", value: "2" },
                        { label: "1 Star (★)", value: "1" },
                      ]}
                    />
                  </Col>

                  {/* Status Filter */}
                  <Col xs="12" sm="6" md="3" lg="2" className="mb-2 mb-md-0">
                    <CustomDropdown
                      value={statusFilter}
                      onChange={(val) => {
                        setStatusFilter(val);
                        setPagination((prev) => ({ ...prev, currentPage: 1 }));
                      }}
                      options={[
                        { label: "All Status", value: "all" },
                        { label: "Active", value: "true" },
                        { label: "Hidden", value: "false" },
                      ]}
                    />
                  </Col>

                  {/* Reset Filters Button */}
                  {(searchTerm || ratingFilter !== "all" || statusFilter !== "all" || reviewTypeFilter !== "all") && (
                    <Col xs="auto">
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={() => {
                          setSearchTerm("");
                          setRatingFilter("all");
                          setStatusFilter("all");
                          setReviewTypeFilter("all");
                          setPagination((prev) => ({ ...prev, currentPage: 1 }));
                        }}
                        title="Reset Filters"
                      >
                        <i className="fas fa-undo mr-1" /> Reset
                      </Button>
                    </Col>
                  )}
                </Row>

                {/* TABLE / LOADER */}
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-5">
                      <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                      <p className="mt-2 text-muted">Loading reviews & ratings...</p>
                    </div>
                  ) : reviews.length === 0 ? (
                    <div className="text-center py-5">
                      <div
                        className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: "64px", height: "64px", color: "#9ca3af" }}
                      >
                        <i className="fas fa-star fa-2x" />
                      </div>
                      <h5 className="text-dark font-weight-bold">No Reviews Found</h5>
                      <p className="text-muted text-sm mb-0">
                        {searchTerm || ratingFilter !== "all" || statusFilter !== "all" || reviewTypeFilter !== "all"
                          ? "No reviews match your selected filter criteria."
                          : "No customer reviews are recorded on the platform yet."}
                      </p>
                    </div>
                  ) : (
                    <Table className="align-items-center table-flush mb-0" hover>
                      <thead className="thead-light">
                        <tr>
                          <th>Sr. No.</th>
                          <th>Booking</th>
                          <th>Traveler</th>
                          <th>Guide</th>
                          <th>Rating</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviews.map((review, index) => (
                          <tr key={review.uuid || review._id}>
                            <td className="font-weight-bold text-xs text-muted">
                              {(pagination.currentPage - 1) * pagination.limit + index + 1}
                            </td>

                            {/* Booking */}
                            <td>
                              <div>
                                <div className="font-weight-bold text-xs text-primary">
                                  {review.booking?.bookingNumber || "Direct Booking"}
                                </div>
                                <small className="text-muted text-capitalize">
                                  {review.booking?.packageName || (review.booking?.bookingType ? review.booking.bookingType.replace(/_/g, " ") : "Guide Tour")}
                                </small>
                              </div>
                            </td>

                            {/* Traveler Column */}
                            <td>
                              <div className="d-flex align-items-center">
                                {review.traveler?.profileImage ? (
                                  <img
                                    src={review.traveler.profileImage}
                                    alt={review.traveler.fullName}
                                    className="rounded-circle mr-2 shadow-sm"
                                    style={{
                                      width: "36px",
                                      height: "36px",
                                      minWidth: "36px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.setProperty("display", "flex", "important");
                                      }
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="rounded-circle bg-gradient-primary text-white align-items-center justify-content-center mr-2 font-weight-bold text-xs shadow-sm"
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    minWidth: "36px",
                                    display: review.traveler?.profileImage ? "none" : "flex",
                                  }}
                                >
                                  {review.traveler?.fullName?.charAt(0)?.toUpperCase() || "T"}
                                </div>
                                <div>
                                  <div className="d-flex align-items-center">
                                    <span className="font-weight-bold text-sm text-dark">
                                      {review.traveler?.fullName || "Traveler"}
                                    </span>
                                    {review.fromType === "traveler" ? (
                                      <Badge
                                        color="primary"
                                        pill
                                        className="ml-2 font-weight-bold"
                                        style={{ fontSize: "10px", padding: "3px 8px" }}
                                        title="Given by Traveler"
                                      >
                                        FROM
                                      </Badge>
                                    ) : (
                                      <Badge
                                        color="light"
                                        pill
                                        className="ml-2 text-muted border font-weight-bold"
                                        style={{ fontSize: "10px", padding: "3px 8px" }}
                                        title="Given to Traveler"
                                      >
                                        TO
                                      </Badge>
                                    )}
                                  </div>
                                  <small className="text-muted d-block">{review.traveler?.email || review.traveler?.mobileNumber || "N/A"}</small>
                                </div>
                              </div>
                            </td>

                            {/* Guide Column */}
                            <td>
                              <div className="d-flex align-items-center">
                                {review.guide?.profileImage ? (
                                  <img
                                    src={review.guide.profileImage}
                                    alt={review.guide.fullName}
                                    className="rounded-circle mr-2 shadow-sm"
                                    style={{
                                      width: "36px",
                                      height: "36px",
                                      minWidth: "36px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.setProperty("display", "flex", "important");
                                      }
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="rounded-circle bg-gradient-info text-white align-items-center justify-content-center mr-2 font-weight-bold text-xs shadow-sm"
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    minWidth: "36px",
                                    display: review.guide?.profileImage ? "none" : "flex",
                                  }}
                                >
                                  {review.guide?.fullName?.charAt(0)?.toUpperCase() || "G"}
                                </div>

                                <div>
                                  <div className="d-flex align-items-center">
                                    <span className="font-weight-bold text-sm text-dark">
                                      {review.guide?.fullName || "Guide"}
                                    </span>
                                    {review.fromType === "guide" ? (
                                      <Badge
                                        color="success"
                                        pill
                                        className="ml-2 font-weight-bold"
                                        style={{ fontSize: "10px", padding: "3px 8px" }}
                                        title="Given by Guide"
                                      >
                                        FROM
                                      </Badge>
                                    ) : (
                                      <Badge
                                        color="light"
                                        pill
                                        className="ml-2 text-muted border font-weight-bold"
                                        style={{ fontSize: "10px", padding: "3px 8px" }}
                                        title="Given to Guide"
                                      >
                                        TO
                                      </Badge>
                                    )}
                                  </div>
                                  <small className="text-muted d-block">{review.guide?.email || review.guide?.mobileNumber || "N/A"}</small>
                                </div>
                              </div>
                            </td>

                            {/* Rating Given */}
                            <td>
                              <span className="font-weight-bold text-sm text-warning">
                                ★ {Number(review.rating || 0).toFixed(1)}
                              </span>
                            </td>

                            {/* Status */}
                            <td>
                              <Badge
                                color={review.isActive ? "success" : "secondary"}
                                pill
                                className="text-capitalize text-xs px-2 py-1"
                              >
                                <i
                                  className="fas fa-circle mr-1"
                                  style={{ fontSize: "7px" }}
                                />
                                {review.isActive ? "Active" : "Hidden"}
                              </Badge>
                            </td>

                            {/* Date */}
                            <td className="text-xs text-muted">
                              {review.createdAt
                                ? new Date(review.createdAt).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                                : "N/A"}
                            </td>

                            {/* Actions Dropdown */}
                            <td className="text-right">
                              <UncontrolledDropdown>
                                <DropdownToggle
                                  className="btn-icon-only text-light"
                                  size="sm"
                                  role="button"
                                  color=""
                                >
                                  <i className="fas fa-ellipsis-v" />
                                </DropdownToggle>
                                <DropdownMenu right className="shadow border-0">
                                  <DropdownItem onClick={() => openViewModal(review)}>
                                    <i className="fas fa-eye text-info mr-2" />
                                    View Full Details
                                  </DropdownItem>
                                  <DropdownItem onClick={() => handleToggleStatus(review.uuid)}>
                                    <i
                                      className={`fas ${review.isActive
                                        ? "fa-eye-slash text-warning"
                                        : "fa-check text-success"
                                        } mr-2`}
                                    />
                                    {review.isActive ? "Hide Review" : "Make Active"}
                                  </DropdownItem>
                                  <DropdownItem divider />
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>

              {/* CARD FOOTER WITH UNIFIED PAGINATION */}
              {!loading && pagination.totalRecords > 0 && (
                <CardFooter className="py-4 border-0">
                  <CustomPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalRecords}
                    itemsPerPage={pagination.limit}
                    onPageChange={handlePageChange}
                    itemName="reviews"
                  />
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* VIEW REVIEW DETAILS MODAL */}
      <Modal
        isOpen={viewModalOpen}
        toggle={closeViewModal}
        centered
        size="lg"
        returnFocus={false}
        className="user-table-user-model"
      >
        <ModalHeader toggle={closeViewModal}>
          <div className="user-modal-header-content">
            <div
              className="user-modal-header-icon"
              style={{ background: "#fffbeb", color: "#f59e0b" }}
            >
              <i className="fas fa-star" />
            </div>
            <div>
              <h4 className="mb-0 text-dark font-weight-bold">Rating & Review Details</h4>
              <small className="text-muted">
                Mutual rating & review breakdown between traveler and guide
              </small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="px-4 py-3">
          {viewLoading ? (
            <div className="text-center py-5">
              <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
              <p className="mt-2 text-muted">Loading review details...</p>
            </div>
          ) : selectedReview ? (() => {
            const travelerReview =
              selectedReview.travelerReview ||
              (selectedReview.fromType === "traveler" ? selectedReview : null);
            const guideReview =
              selectedReview.guideReview ||
              (selectedReview.fromType === "guide" ? selectedReview : null);

            const travelerUser = selectedReview.traveler || {};
            const guideUser = selectedReview.guide || {};

            return (
              <div>
                {/* Associated Booking Summary Card */}
                {selectedReview.booking && (
                  <div
                    className="p-3 mb-4 rounded border"
                    style={{
                      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      borderColor: "#e2e8f0",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-muted text-xs text-uppercase font-weight-bold d-flex align-items-center">
                        <i className="fas fa-ticket-alt mr-2 text-primary" /> Associated Booking Details
                      </span>
                      <Badge color="info" pill className="text-capitalize px-3 py-1 font-weight-600">
                        {selectedReview.booking.status || "Completed"}
                      </Badge>
                    </div>
                    <Row>
                      <Col sm="6" md="3" className="mb-2">
                        <small className="text-muted d-block" style={{ fontSize: "11px" }}>Booking Number</small>
                        <strong className="text-primary font-weight-bold text-sm">
                          <i className="fas fa-hashtag mr-1" />
                          {selectedReview.booking.bookingNumber || "N/A"}
                        </strong>
                      </Col>
                      <Col sm="6" md="3" className="mb-2">
                        <small className="text-muted d-block" style={{ fontSize: "11px" }}>Tour / Package</small>
                        <strong className="text-dark font-weight-bold text-sm text-truncate d-block">
                          {selectedReview.booking.packageName || (selectedReview.booking.bookingType ? selectedReview.booking.bookingType.replace(/_/g, " ") : "Direct Guide Booking")}
                        </strong>
                      </Col>
                      <Col sm="6" md="3" className="mb-2">
                        <small className="text-muted d-block" style={{ fontSize: "11px" }}>Booking Date</small>
                        <strong className="text-dark font-weight-bold text-sm">
                          <i className="far fa-calendar-alt mr-1 text-muted" />
                          {selectedReview.booking.bookingDate
                            ? new Date(selectedReview.booking.bookingDate).toLocaleDateString()
                            : "N/A"}
                        </strong>
                      </Col>
                      <Col sm="6" md="3" className="mb-2">
                        <small className="text-muted d-block" style={{ fontSize: "11px" }}>Total Price</small>
                        <strong className="text-success font-weight-bold text-sm">
                          ${selectedReview.booking.totalPrice || 0}
                        </strong>
                      </Col>
                    </Row>
                  </div>
                )}

                {/* Section Title */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h5 className="mb-0 text-dark font-weight-bold">
                      <i className="fas fa-exchange-alt text-primary mr-2" />
                      Mutual Ratings & Reviews
                    </h5>
                    <small className="text-muted">
                      Comparison of who rated whom for this experience
                    </small>
                  </div>
                </div>

                {/* 2 PROMINENT DIVS: TRAVELER RATING & GUIDE RATING */}
                <Row className="mb-3">
                  {/* DIV 1: Traveler's Rating to Guide */}
                  <Col md="6" className="mb-3">
                    <div
                      className="rounded h-100 overflow-hidden"
                      style={{
                        backgroundColor: "#ffffff",
                        border: travelerReview ? "1.5px solid #6366f1" : "1.5px dashed #cbd5e1",
                        boxShadow: travelerReview ? "0 4px 16px rgba(99, 102, 241, 0.1)" : "none",
                      }}
                    >
                      {/* Card Top Header */}
                      <div
                        className="p-3 d-flex align-items-center justify-content-between"
                        style={{
                          background: travelerReview
                            ? "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)"
                            : "#f8fafc",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm font-weight-bold"
                            style={{ width: "28px", height: "28px", background: "#6366f1", color: "#ffffff", fontSize: "12px" }}
                          >
                            <i className="fas fa-paper-plane" />
                          </div>
                          <div>
                            <span className="font-weight-bold text-dark d-block" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                              Traveler ➔ Guide Review
                            </span>
                            <small className="text-muted" style={{ fontSize: "11px" }}>
                              Given by <strong>{travelerUser.fullName || "Traveler"}</strong> to <strong>{guideUser.fullName || "Guide"}</strong>
                            </small>
                          </div>
                        </div>

                        <div>
                          {travelerReview ? (
                            <div className="d-flex align-items-center gap-1">
                              <Badge
                                color={travelerReview.isActive !== false ? "success" : "secondary"}
                                pill
                                className="px-2 py-1 mr-1"
                              >
                                {travelerReview.isActive !== false ? "Active" : "Hidden"}
                              </Badge>
                              <Button
                                color="link"
                                size="sm"
                                className="p-0 text-muted"
                                title="Toggle Visibility"
                                onClick={() => handleToggleStatus(travelerReview.uuid)}
                              >
                                <i className={`fas ${travelerReview.isActive !== false ? "fa-eye-slash text-danger" : "fa-eye text-success"}`} />
                              </Button>
                            </div>
                          ) : (
                            <Badge color="light" pill className="text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                              Not Rated Yet
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-3">
                        {/* WHO RATED WHOM FLOW */}
                        <div
                          className="p-2 mb-3 rounded d-flex align-items-center justify-content-between"
                          style={{
                            background: travelerReview ? "#f8fafc" : "#fafafa",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {/* Giver: Traveler */}
                          <div className="d-flex align-items-center" style={{ flex: "1 1 0", minWidth: 0 }}>
                            {travelerUser.profileImage ? (
                              <img
                                src={travelerUser.profileImage}
                                alt={travelerUser.fullName}
                                className="rounded-circle mr-2 shadow-sm"
                                style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                className="avatar avatar-sm rounded-circle mr-2 bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                              >
                                {travelerUser.fullName ? travelerUser.fullName.charAt(0).toUpperCase() : "T"}
                              </div>
                            )}
                            <div className="overflow-hidden">
                              <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                {travelerUser.fullName || "Traveler"}
                              </span>
                              <Badge color="primary" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                Giver (Traveler)
                              </Badge>
                            </div>
                          </div>

                          {/* Arrow Connector */}
                          <div className="text-center px-1 d-flex flex-column align-items-center" style={{ minWidth: "85px" }}>
                            <span className="text-muted text-uppercase" style={{ fontSize: "9px", fontWeight: "700" }}>
                              {travelerReview ? "Rates" : "Pending"}
                            </span>
                            <div className="d-flex align-items-center text-primary" style={{ fontSize: "13px" }}>
                              <span style={{ height: "2px", width: "20px", background: "#6366f1", display: "inline-block" }}></span>
                              <i className="fas fa-chevron-right" style={{ color: "#6366f1", marginLeft: "-2px" }} />
                            </div>
                          </div>

                          {/* Receiver: Guide */}
                          <div className="d-flex align-items-center justify-content-end text-right" style={{ flex: "1 1 0", minWidth: 0 }}>
                            <div className="overflow-hidden mr-2">
                              <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                {guideUser.fullName || "Guide"}
                              </span>
                              <Badge color="success" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                Receiver (Guide)
                              </Badge>
                            </div>
                            {guideUser.profileImage ? (
                              <img
                                src={guideUser.profileImage}
                                alt={guideUser.fullName}
                                className="rounded-circle shadow-sm"
                                style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                className="avatar avatar-sm rounded-circle bg-gradient-success text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                              >
                                {guideUser.fullName ? guideUser.fullName.charAt(0).toUpperCase() : "G"}
                              </div>
                            )}
                          </div>
                        </div>

                        {travelerReview ? (
                          <div>
                            {/* Star Rating Score Bar */}
                            <div
                              className="p-2 mb-2 rounded d-flex align-items-center justify-content-between"
                              style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
                            >
                              <div className="d-flex align-items-center">
                                <div className="mr-2">{renderStars(travelerReview.rating)}</div>
                                <span className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                                  {Number(travelerReview.rating).toFixed(1)} / 5.0
                                </span>
                              </div>
                              <small className="text-muted font-weight-500" style={{ fontSize: "11px" }}>
                                <i className="far fa-clock mr-1" />
                                {travelerReview.createdAt
                                  ? new Date(travelerReview.createdAt).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "N/A"}
                              </small>
                            </div>

                            {/* Written Review Feedback */}
                            <div>
                              <small className="text-muted text-uppercase font-weight-bold d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                                Written Feedback:
                              </small>
                              <div
                                className="p-3 rounded bg-white text-dark shadow-xs"
                                style={{
                                  fontSize: "13px",
                                  lineHeight: "1.6",
                                  border: "1px solid #e2e8f0",
                                  borderLeft: "3px solid #6366f1",
                                  minHeight: "56px",
                                  fontStyle: travelerReview.review ? "normal" : "italic",
                                }}
                              >
                                {travelerReview.review ? (
                                  <>
                                    <i className="fas fa-quote-left text-muted mr-2" style={{ fontSize: "11px" }} />
                                    {travelerReview.review}
                                  </>
                                ) : (
                                  <span className="text-muted">No written feedback was provided.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 px-2">
                            <div
                              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                              style={{ width: "42px", height: "42px", background: "#f1f5f9", color: "#94a3b8" }}
                            >
                              <i className="far fa-star fa-lg" />
                            </div>
                            <h6 className="font-weight-bold text-dark mb-1" style={{ fontSize: "13px" }}>
                              Traveler Has Not Rated Yet
                            </h6>
                            <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                              <strong>{travelerUser.fullName || "Traveler"}</strong> has not submitted a rating or review for <strong>{guideUser.fullName || "Guide"}</strong> yet.
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* DIV 2: Guide's Rating to Traveler */}
                  <Col md="6" className="mb-3">
                    <div
                      className="rounded h-100 overflow-hidden"
                      style={{
                        backgroundColor: "#ffffff",
                        border: guideReview ? "1.5px solid #10b981" : "1.5px dashed #cbd5e1",
                        boxShadow: guideReview ? "0 4px 16px rgba(16, 185, 129, 0.1)" : "none",
                      }}
                    >
                      {/* Card Top Header */}
                      <div
                        className="p-3 d-flex align-items-center justify-content-between"
                        style={{
                          background: guideReview
                            ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                            : "#f8fafc",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm font-weight-bold"
                            style={{ width: "28px", height: "28px", background: "#10b981", color: "#ffffff", fontSize: "12px" }}
                          >
                            <i className="fas fa-paper-plane" />
                          </div>
                          <div>
                            <span className="font-weight-bold text-dark d-block" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                              Guide ➔ Traveler Review
                            </span>
                            <small className="text-muted" style={{ fontSize: "11px" }}>
                              Given by <strong>{guideUser.fullName || "Guide"}</strong> to <strong>{travelerUser.fullName || "Traveler"}</strong>
                            </small>
                          </div>
                        </div>

                        <div>
                          {guideReview ? (
                            <div className="d-flex align-items-center gap-1">
                              <Badge
                                color={guideReview.isActive !== false ? "success" : "secondary"}
                                pill
                                className="px-2 py-1 mr-1"
                              >
                                {guideReview.isActive !== false ? "Active" : "Hidden"}
                              </Badge>
                              <Button
                                color="link"
                                size="sm"
                                className="p-0 text-muted"
                                title="Toggle Visibility"
                                onClick={() => handleToggleStatus(guideReview.uuid)}
                              >
                                <i className={`fas ${guideReview.isActive !== false ? "fa-eye-slash text-danger" : "fa-eye text-success"}`} />
                              </Button>
                            </div>
                          ) : (
                            <Badge color="light" pill className="text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                              Not Rated Yet
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-3">
                        {/* WHO RATED WHOM FLOW */}
                        <div
                          className="p-2 mb-3 rounded d-flex align-items-center justify-content-between"
                          style={{
                            background: guideReview ? "#f8fafc" : "#fafafa",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {/* Giver: Guide */}
                          <div className="d-flex align-items-center" style={{ flex: "1 1 0", minWidth: 0 }}>
                            {guideUser.profileImage ? (
                              <img
                                src={guideUser.profileImage}
                                alt={guideUser.fullName}
                                className="rounded-circle mr-2 shadow-sm"
                                style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                className="avatar avatar-sm rounded-circle mr-2 bg-gradient-success text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                              >
                                {guideUser.fullName ? guideUser.fullName.charAt(0).toUpperCase() : "G"}
                              </div>
                            )}
                            <div className="overflow-hidden">
                              <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                {guideUser.fullName || "Guide"}
                              </span>
                              <Badge color="success" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                Giver (Guide)
                              </Badge>
                            </div>
                          </div>

                          {/* Arrow Connector */}
                          <div className="text-center px-1 d-flex flex-column align-items-center" style={{ minWidth: "85px" }}>
                            <span className="text-muted text-uppercase" style={{ fontSize: "9px", fontWeight: "700" }}>
                              {guideReview ? "Rates" : "Pending"}
                            </span>
                            <div className="d-flex align-items-center text-success" style={{ fontSize: "13px" }}>
                              <span style={{ height: "2px", width: "20px", background: "#10b981", display: "inline-block" }}></span>
                              <i className="fas fa-chevron-right" style={{ color: "#10b981", marginLeft: "-2px" }} />
                            </div>
                          </div>

                          {/* Receiver: Traveler */}
                          <div className="d-flex align-items-center justify-content-end text-right" style={{ flex: "1 1 0", minWidth: 0 }}>
                            <div className="overflow-hidden mr-2">
                              <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                {travelerUser.fullName || "Traveler"}
                              </span>
                              <Badge color="primary" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                Receiver (Traveler)
                              </Badge>
                            </div>
                            {travelerUser.profileImage ? (
                              <img
                                src={travelerUser.profileImage}
                                alt={travelerUser.fullName}
                                className="rounded-circle shadow-sm"
                                style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                className="avatar avatar-sm rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                              >
                                {travelerUser.fullName ? travelerUser.fullName.charAt(0).toUpperCase() : "T"}
                              </div>
                            )}
                          </div>
                        </div>

                        {guideReview ? (
                          <div>
                            {/* Star Rating Score Bar */}
                            <div
                              className="p-2 mb-2 rounded d-flex align-items-center justify-content-between"
                              style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
                            >
                              <div className="d-flex align-items-center">
                                <div className="mr-2">{renderStars(guideReview.rating)}</div>
                                <span className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                                  {Number(guideReview.rating).toFixed(1)} / 5.0
                                </span>
                              </div>
                              <small className="text-muted font-weight-500" style={{ fontSize: "11px" }}>
                                <i className="far fa-clock mr-1" />
                                {guideReview.createdAt
                                  ? new Date(guideReview.createdAt).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "N/A"}
                              </small>
                            </div>

                            {/* Written Review Feedback */}
                            <div>
                              <small className="text-muted text-uppercase font-weight-bold d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                                Written Feedback:
                              </small>
                              <div
                                className="p-3 rounded bg-white text-dark shadow-xs"
                                style={{
                                  fontSize: "13px",
                                  lineHeight: "1.6",
                                  border: "1px solid #e2e8f0",
                                  borderLeft: "3px solid #10b981",
                                  minHeight: "56px",
                                  fontStyle: guideReview.review ? "normal" : "italic",
                                }}
                              >
                                {guideReview.review ? (
                                  <>
                                    <i className="fas fa-quote-left text-muted mr-2" style={{ fontSize: "11px" }} />
                                    {guideReview.review}
                                  </>
                                ) : (
                                  <span className="text-muted">No written feedback was provided.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 px-2">
                            <div
                              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                              style={{ width: "42px", height: "42px", background: "#f1f5f9", color: "#94a3b8" }}
                            >
                              <i className="far fa-star fa-lg" />
                            </div>
                            <h6 className="font-weight-bold text-dark mb-1" style={{ fontSize: "13px" }}>
                              Guide Has Not Rated Yet
                            </h6>
                            <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                              <strong>{guideUser.fullName || "Guide"}</strong> has not submitted a rating or review for <strong>{travelerUser.fullName || "Traveler"}</strong> yet.
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            );
          })() : null}
        </ModalBody>

        <ModalFooter className="border-0 pt-0">
          <Button color="secondary" size="sm" onClick={closeViewModal}>
            Close
          </Button>
          {selectedReview && (
            <Button
              color={selectedReview.isActive ? "warning" : "success"}
              size="sm"
              onClick={() => handleToggleStatus(selectedReview.uuid)}
            >
              {selectedReview.isActive ? "Hide Review" : "Make Active"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Reviews;